import {
  type ComponentProps,
  type FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  ButtonGroup,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  CloseIcon,
  Column,
  Portal,
  Row,
  SelectorIcon,
  ToggleButton,
  ToggleButtonGroup,
  useDisclosure,
  useToast,
} from "@hightouch/htui";
import { type ExtendedOption, toExtendedOption } from "@hightouch/formkit";

import {
  type JsonColumnProps,
  type Mapping,
  MappingType,
  type OverwriteStandardInputWithArrayProps,
  type Props,
  type SelectedOptionContext,
} from "src/formkit/components/types";
import { ArrayPropertiesInput } from "./array-properties-input";
import { Value } from "./mapper-value";
import { isValueEmpty } from "./utils";
import { resolveVariableScope } from "src/formkit/components/mapper-v2/variable-options";
import { variableMappingLabel } from "./variable-labels";
import { StandardInput } from "./standard-input";
import { INPUTS } from "./types";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { TemplatePreviewContext } from "./template-preview-context";

type ValueProp = {
  children?: React.ReactNode;
  from?: string;
  template?: string;
  to?: string;
  type?: string;
  value?: string;
  variable?: string;
};

export const LegacyMapper: FC<
  Readonly<
    Props & {
      enableInLineMapper?: boolean;
      isClearable?: boolean;
      jsonColumnProperties: JsonColumnProps;
      mappingTypes?: MappingType[];
      onChangeJsonColumnProperties: React.Dispatch<
        React.SetStateAction<JsonColumnProps>
      >;
      onReloadEligibleInlineMapperColumns: (
        currentSelectedColumn?: string,
      ) => void;
      parentMapping?: Mapping;
      popoverMaxWidth?: ComponentProps<typeof PopoverContent>["maxWidth"];
      popoverPlacement?: ComponentProps<typeof Popover>["placement"];
      selectedOption: SelectedOptionContext | ExtendedOption | undefined;
    } & OverwriteStandardInputWithArrayProps
  >
> = ({
  columnOptions,
  enableInLineMapper,
  isClearable,
  isDisabled = false,
  isError,
  jsonColumnProperties,
  mappingTypes: mappingTypesProp,
  onChange,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  overwriteColumnsWithArrayProps,
  parentMapping,
  placeholder,
  popoverMaxWidth = "786px",
  popoverPlacement = "bottom-start",
  selectedOption,
  templates,
  ...props
}) => {
  const [value, setValue] = useState<ValueProp>(props.value || {});
  const { isOpen, onClose, onToggle } = useDisclosure();
  const searchRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { model, mappingTypesOverride, variableScope } = useFormkitContext();
  const templatePreviewCtx = useContext(TemplatePreviewContext);

  // A form-wide restriction (e.g. journey sync nodes, whose realtime worker
  // only supports trait columns, static values, and variables) beats whatever
  // the destination's formkit definition enables per field.
  const mappingTypes = mappingTypesOverride ?? mappingTypesProp;

  const standardShouldBeEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.STANDARD)
      : true;
  const standardEnabled = standardShouldBeEnabled;
  const staticEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.STATIC)
      : true;
  const variableEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.VARIABLE)
      : Boolean(model);
  const templateEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.TEMPLATE)
      : true;
  const objectEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.OBJECT)
      : true;
  const arrayShouldBeEnabled =
    mappingTypes && mappingTypes.length
      ? mappingTypes.includes(MappingType.ARRAY)
      : true;
  const arrayEnabled = arrayShouldBeEnabled;

  useEffect(() => {
    setValue(props.value || {});
  }, [props.value]);

  const scope = resolveVariableScope({ variableScope, model });

  const fieldType: string = value?.type ?? MappingType.STANDARD;

  let MappingInput;
  if (fieldType === MappingType.STANDARD && overwriteColumnsWithArrayProps) {
    MappingInput = ArrayPropertiesInput;
  } else {
    MappingInput =
      fieldType && fieldType in INPUTS ? INPUTS[fieldType] : StandardInput;
  }
  const selectedType = fieldType;

  const extractedType = toExtendedOption(selectedOption)?.extendedType?.type;
  const isFieldDisabled =
    isDisabled || props.value?.from?.type === MappingType.BOOSTED;

  const shouldEnableArrayInlineMapper =
    (enableInLineMapper && extractedType === "ARRAY") ||
    (enableInLineMapper && !extractedType);

  const shouldEnableObjectInlineMapper =
    (enableInLineMapper && extractedType === "OBJECT") ||
    (enableInLineMapper && !extractedType);

  const handleApply = (onClose, valueToApply) => {
    const newValue = { ...value, ...valueToApply };
    if (newValue?.type === MappingType.ARRAY && !newValue?.from) {
      toast({
        id: "array-inline-mapper",
        title: "Please select a column before proceeding.",
        variant: "error",
      });
    } else if (
      parentMapping?.type === MappingType.ARRAY &&
      newValue?.type === MappingType.ARRAY &&
      parentMapping?.from !== newValue?.from
    ) {
      toast({
        id: "array-inline-mapper",
        title: "You must select the same column as the top-level mapping.",
        variant: "error",
      });
    } else {
      if (newValue.type === "boosted") {
        // 'boosted' is not a top level type
        // the 'boosted' type lives in `newValue`, so set the
        // top level type back to 'standard'
        onChange({ ...newValue, type: "standard" });
      } else {
        onChange(newValue);
      }

      onClose();
    }
  };

  const onChangeToUse =
    fieldType === MappingType.OBJECT ||
    fieldType === MappingType.STATIC ||
    fieldType === MappingType.TEMPLATE
      ? setValue
      : handleApply;

  const toggleButtons = [
    {
      enabled: standardEnabled,
      label: "Column",
      value: MappingType.STANDARD,
    },
    {
      enabled: staticEnabled,
      label: "Static value",
      value: MappingType.STATIC,
    },
    {
      enabled: variableEnabled,
      label: variableMappingLabel(scope),
      value: MappingType.VARIABLE,
    },
    {
      enabled: templateEnabled,
      label: "Template",
      value: MappingType.TEMPLATE,
    },
    {
      enabled: shouldEnableObjectInlineMapper && objectEnabled,
      label: "Create an object",
      value: MappingType.OBJECT,
    },
    {
      enabled: shouldEnableArrayInlineMapper && arrayEnabled,
      label: "Create an array",
      value: MappingType.ARRAY,
    },
  ];

  const enabledToggleButtons = toggleButtons.filter((button) => button.enabled);
  const fieldValue = { ...props.value };
  if (fieldType) fieldValue.type = fieldType;

  const borderColor = isFieldDisabled
    ? "base.borderDisabled"
    : isError
      ? "danger.base"
      : isOpen
        ? "primary.base"
        : "base.border";

  return (
    <Row align="center" gap={1.5}>
      <Box flex={1} minWidth={0}>
        <Popover
          isLazy
          preventOverflow
          closeOnBlur={!templatePreviewCtx?.isPreviewOpen}
          initialFocusRef={searchRef}
          isOpen={isOpen}
          onClose={onClose}
          placement={popoverPlacement}
        >
          <PopoverTrigger>
            <Row
              as="button"
              aria-label="Select mapping type"
              disabled={isFieldDisabled}
              alignItems="center"
              bg={
                isFieldDisabled
                  ? "base.backgroundDisabled"
                  : isOpen
                    ? "white"
                    : "base.lightBackground"
              }
              borderWidth="hairline"
              borderStyle="solid"
              borderColor={borderColor}
              borderRadius="lg"
              boxShadow={isFieldDisabled ? "none" : "buttonShadowLight"}
              color={isFieldDisabled ? "text.disabled" : undefined}
              cursor={isFieldDisabled ? "not-allowed" : undefined}
              display="flex"
              justifyContent="space-between"
              minHeight={8}
              px={2}
              py={1}
              transitionProperty="common"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              width="100%"
              _active={{
                bg: isFieldDisabled ? undefined : "white",
                borderColor: borderColor,
              }}
              _hover={{
                bg: isFieldDisabled ? undefined : "white",
                borderColor: borderColor,
              }}
              _focus={{ outline: "none" }}
              _focusVisible={{
                boxShadow: isFieldDisabled ? undefined : "outline",
              }}
              onClick={isFieldDisabled ? undefined : onToggle}
            >
              <Value
                isDisabled={isFieldDisabled}
                placeholder={placeholder}
                value={fieldValue}
                columnOptions={columnOptions}
                selectedOptionSemanticType={
                  toExtendedOption(selectedOption)?.extendedType
                    ?.semanticColumnType
                }
              />
              {isClearable && !isValueEmpty(props.value) ? (
                <Box
                  as={CloseIcon}
                  color="text.primary"
                  cursor="pointer"
                  fontSize="16px"
                  onClick={onChange}
                />
              ) : (
                <Box
                  as={SelectorIcon}
                  color={isFieldDisabled ? "text.disabled" : "text.secondary"}
                  fontSize="20px"
                />
              )}
            </Row>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              height="md"
              maxWidth={popoverMaxWidth}
              overflow="hidden"
              width="100vw"
            >
              <PopoverBody
                p={0}
                height="100%"
                display="flex"
                flexDirection="column"
              >
                <>
                  <Column flex="1" minHeight="0">
                    <Box p={3}>
                      <ToggleButtonGroup
                        onChange={(type) => {
                          // Preserve state when switching between array types
                          if (
                            type === MappingType.ARRAY &&
                            fieldType === MappingType.ARRAY
                          ) {
                            setValue({ ...value, type });
                          } else {
                            setValue({ type });
                          }
                        }}
                        value={selectedType || enabledToggleButtons?.[0]?.value}
                      >
                        {enabledToggleButtons.map((button) => (
                          <ToggleButton
                            key={button.value}
                            label={button.label}
                            value={button.value}
                          />
                        ))}
                      </ToggleButtonGroup>
                    </Box>

                    <MappingInput
                      scope={scope}
                      onClose={onClose}
                      columnOptions={columnOptions}
                      jsonColumnProperties={jsonColumnProperties}
                      onChange={onChangeToUse}
                      onChangeJsonColumnProperties={
                        onChangeJsonColumnProperties
                      }
                      onReloadEligibleInlineMapperColumns={
                        onReloadEligibleInlineMapperColumns
                      }
                      overwriteColumnsWithArrayProps={
                        overwriteColumnsWithArrayProps
                      }
                      ref={searchRef}
                      templates={templates}
                      value={value}
                    />
                  </Column>

                  {(value.type === MappingType.OBJECT ||
                    value.type === MappingType.STATIC ||
                    value.type === MappingType.TEMPLATE) && (
                    <Row alignItems="center" justifyContent="right" p={3}>
                      <ButtonGroup>
                        <Button
                          onClick={() => handleApply(onClose, value)}
                          variant="primary"
                        >
                          Apply
                        </Button>
                      </ButtonGroup>
                    </Row>
                  )}
                </>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    </Row>
  );
};
